import { joinEmbedSnippetUrl, withEmbedFlag } from './config';

export const DEFAULT_EMBED_SETTINGS = {
    width: '100%',
    height: '600px',
    maxWidth: '',
    pageBackground: '#ffffff',
    transparent: false,
    showBorder: true,
    borderColor: '#e5e7eb',
    borderRadius: '8px',
    contentPadding: '40px'
};

export const DEFAULT_THEME = {
    primaryColor: '#2563eb',
    backgroundColor: '#ffffff',
    surfaceColor: '#ffffff',
    textColor: '#1f2937',
    fontFamily: 'Inter',
    borderRadius: '8px'
};

export const normalizeEmbedSettings = (embed = {}) => ({
    ...DEFAULT_EMBED_SETTINGS,
    ...(embed && typeof embed === 'object' ? embed : {})
});

export const getEmbedPageBackground = (embed = {}) => {
    const settings = normalizeEmbedSettings(embed);
    return settings.transparent ? 'transparent' : (settings.pageBackground || DEFAULT_EMBED_SETTINGS.pageBackground);
};

export const buildThemeFromTokens = (tokens = {}, embed = {}) => {
    const settings = normalizeEmbedSettings(embed);
    return {
        primaryColor: tokens.primaryColor || DEFAULT_THEME.primaryColor,
        backgroundColor: getEmbedPageBackground(settings),
        surfaceColor: tokens.backgroundColor || DEFAULT_THEME.surfaceColor,
        textColor: tokens.textColor || DEFAULT_THEME.textColor,
        fontFamily: tokens.fontFamily || DEFAULT_THEME.fontFamily,
        borderRadius: tokens.borderRadius || DEFAULT_THEME.borderRadius
    };
};

export const normalizeTheme = (theme = {}, embed = {}, styleTokens = {}) => {
    const settings = normalizeEmbedSettings(embed);
    return {
        ...DEFAULT_THEME,
        ...(theme && typeof theme === 'object' ? theme : {}),
        primaryColor: theme?.primaryColor || styleTokens.primaryColor || DEFAULT_THEME.primaryColor,
        backgroundColor: getEmbedPageBackground(settings),
        surfaceColor: theme?.surfaceColor || styleTokens.backgroundColor || DEFAULT_THEME.surfaceColor,
        textColor: theme?.textColor || styleTokens.textColor || DEFAULT_THEME.textColor,
        fontFamily: theme?.fontFamily || styleTokens.fontFamily || DEFAULT_THEME.fontFamily,
        borderRadius: theme?.borderRadius || styleTokens.borderRadius || DEFAULT_THEME.borderRadius
    };
};

const toCssStyleString = (style) => Object.entries(style)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => `${key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)}:${value}`)
    .join('; ');

export const getIframeBoxStyle = (embed = {}) => {
    const settings = normalizeEmbedSettings(embed);
    const style = {
        border: settings.showBorder ? `1px solid ${settings.borderColor || '#e5e7eb'}` : 'none',
        borderRadius: settings.borderRadius || '8px'
    };

    if (settings.maxWidth) {
        style.maxWidth = settings.maxWidth;
    }

    if (settings.transparent) {
        style.background = 'transparent';
    }

    return style;
};

export const getEmbedPreviewFrameStyle = (embed = {}) => {
    const settings = normalizeEmbedSettings(embed);
    const pageBackground = getEmbedPageBackground(settings);

    return {
        width: settings.width || '100%',
        height: settings.height || '600px',
        maxWidth: settings.maxWidth || undefined,
        boxSizing: 'border-box',
        border: settings.showBorder ? `1px solid ${settings.borderColor || '#e5e7eb'}` : '1px dashed var(--border)',
        borderRadius: settings.borderRadius || '8px',
        overflow: 'auto',
        padding: settings.contentPadding || '40px',
        background: pageBackground,
        flexShrink: 0
    };
};

const resolveFrameUrl = ({ token, embedBaseUrl, embedFrameUrl }) => {
    if (embedFrameUrl) return withEmbedFlag(embedFrameUrl);
    return joinEmbedSnippetUrl(embedBaseUrl, token);
};

export const buildIframeSnippet = ({ token, embedBaseUrl, embedFrameUrl, embed } = {}) => {
    if (!token && !embedFrameUrl) return '';

    const settings = normalizeEmbedSettings(embed);
    const src = resolveFrameUrl({ token, embedBaseUrl, embedFrameUrl });
    const style = toCssStyleString(getIframeBoxStyle(settings));
    const transparency = settings.transparent ? ' allowtransparency="true"' : '';

    return `<iframe src="${src}" width="${settings.width}" height="${settings.height}" style="${style}"${transparency}></iframe>`;
};

export const buildWidgetSnippet = ({ token, embedBaseUrl, embed } = {}) => {
    if (!token) return '';

    const settings = normalizeEmbedSettings(embed);
    const widgetFrameBase = (embedBaseUrl || '').replace(/\/+$/, '');
    const border = settings.showBorder
        ? `1px solid ${settings.borderColor || '#e5e7eb'}`
        : 'none';
    const dataAttrs = [
        `id="caas-widget"`,
        `data-token="${token}"`,
        `data-width="${settings.width}"`,
        `data-height="${settings.height}"`,
        `data-border="${border}"`,
        `data-border-radius="${settings.borderRadius || '8px'}"`
    ];

    if (settings.maxWidth) {
        dataAttrs.push(`data-max-width="${settings.maxWidth}"`);
    }
    if (settings.transparent) {
        dataAttrs.push('data-transparent="true"');
    }

    return `<div ${dataAttrs.join(' ')}></div>
<script>
  (function() {
    var el = document.getElementById('caas-widget');
    var token = el.getAttribute('data-token');
    var iframe = document.createElement('iframe');
    iframe.src = '${widgetFrameBase}/?token=' + encodeURIComponent(token) + '&embed=1';
    iframe.style.width = el.getAttribute('data-width') || '100%';
    iframe.style.height = el.getAttribute('data-height') || '600px';
    var maxWidth = el.getAttribute('data-max-width');
    if (maxWidth) iframe.style.maxWidth = maxWidth;
    iframe.style.border = el.getAttribute('data-border') || '1px solid #e5e7eb';
    iframe.style.borderRadius = el.getAttribute('data-border-radius') || '8px';
    if (el.getAttribute('data-transparent') === 'true') {
      iframe.style.background = 'transparent';
      iframe.setAttribute('allowtransparency', 'true');
    }
    el.appendChild(iframe);
  })();
</script>`;
};

export const buildEmbedSnippets = (args = {}) => ({
    iframe: buildIframeSnippet(args),
    widget: buildWidgetSnippet(args)
});
